import type { RangeType } from '~/_shared/utils/range/range.helpers';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { type BoundaryGroup } from '../../../../../boundary/boundary.types';
import {
  type BOUNDARY_MAX_VALUE_ID, type BOUNDARY_MIN_VALUE_ID,
} from '../../../../../boundary/settings/boundarySettings.helpers';
import { BoundaryFillSettingsGroupComponent } from './boundaryFillSettingsGroupComponent';
import { BoundaryFillSettingsNumericComponent } from './boundaryFillSettingsNumericComponent';

type BoundarySettingsContentCommonProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  boundaryGroup: BoundaryGroup;
  boundaryPermissions: BoundaryGroupPermissions;
  valueIsInitial: boolean;
  onBoundaryTerritoryGroupChange: (boundaryTerritoryGroup: BoundaryTerritoryGroup) => void;
  onFormValidityChange: (isValid: boolean) => void;
  isValid: boolean;
}>;

export enum BoundaryFillSettingsLayout {
  Numeric,
  Group,
}

export type BoundaryFillSettingsNumericProps = Readonly<{
  boundaryGroupName: string;
  disableRangeTypeChange: boolean;
  isDecimal: boolean;
  layout: BoundaryFillSettingsLayout.Numeric;
  max: number | typeof BOUNDARY_MAX_VALUE_ID;
  min: number | typeof BOUNDARY_MIN_VALUE_ID;
  onNumberOfRangesChange: (numberOfRanges: number, style: any, rangeType: RangeType) => void;
  onRecalculateRangesClick: () => void;
}>;

export type BoundaryFillSettingsGroupProps = Readonly<{
  layout: BoundaryFillSettingsLayout.Group;
  boundaryGroupName: string;
  onBoundaryGroupNameChange?: (newName: string) => void;
  onRecalculateRangesClick?: () => void;
  onRestoreDefaultColors: () => void;
}>;

export type BoundarySettingsContentProps = BoundarySettingsContentCommonProps &
  (BoundaryFillSettingsNumericProps | BoundaryFillSettingsGroupProps);

export const BoundaryFillSettingsContentComponent = (props: BoundarySettingsContentProps) => {
  switch (props.layout) {
    case BoundaryFillSettingsLayout.Numeric:
      return (
        <BoundaryFillSettingsNumericComponent
          // drop component when the group changes
          key={`boundary-settings-${props.boundaryTerritoryGroup.boundaryTerritoryGroupId}`}
          boundaryTerritoryGroup={props.boundaryTerritoryGroup}
          valueIsInitial={props.valueIsInitial}
          disableRangeTypeChange={props.disableRangeTypeChange}
          isDecimal={props.isDecimal}
          max={props.max}
          min={props.min}
          onBoundaryTerritoryGroupChange={props.onBoundaryTerritoryGroupChange}
          onNumberOfRangesChange={props.onNumberOfRangesChange}
          setIsFormValid={props.onFormValidityChange}
          isValid={props.isValid}
          onRecalculateRangesClick={props.onRecalculateRangesClick}
        />
      );
    case BoundaryFillSettingsLayout.Group:
      return (
        <BoundaryFillSettingsGroupComponent
          // drop component when the group changes
          key={`boundary-settings-${props.boundaryTerritoryGroup.boundaryTerritoryGroupId}`}
          boundaryTerritoryGroup={props.boundaryTerritoryGroup}
          onBoundaryTerritoryGroupChange={props.onBoundaryTerritoryGroupChange}
          setIsFormValid={props.onFormValidityChange}
          restoreDefaultColors={props.onRestoreDefaultColors}
          isValid={props.isValid}
        />
      );
    default:
      break;
  }

  return null;
};
